import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';

import { listWatchers, unwatchIssue, watchIssue } from '../../api/notifications';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { Tooltip } from '../../components/Tooltip';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './IssueWatchers.module.css';

/** Наблюдатели задачи и кнопка «Следить» (UC-ISS-10, docs/06 §3.2). */
export function IssueWatchers({ issueKey }: { readonly issueKey: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const watchersKey = queryKeys.notifications.watchers(issueKey);

  const watchersQuery = useQuery({
    queryKey: watchersKey,
    queryFn: ({ signal }) => listWatchers(issueKey, signal),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: watchersKey });
  };

  const watchMutation = useMutation({
    mutationFn: () => (watchersQuery.data?.isWatching ? unwatchIssue(issueKey) : watchIssue(issueKey)),
    onSuccess: (state) => {
      invalidate();
      toast.success(state.isWatching ? 'Вы следите за задачей' : 'Вы больше не следите');
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const watchers = watchersQuery.data?.items ?? [];
  const isWatching = watchersQuery.data?.isWatching ?? false;

  return (
    <div className={styles['wrap']}>
      <Button
        variant={isWatching ? 'secondary' : 'primary'}
        size="sm"
        data-testid="watch-button"
        loading={watchMutation.isPending}
        disabled={watchersQuery.isPending}
        icon={isWatching ? <EyeOff size={14} /> : <Eye size={14} />}
        onClick={() => watchMutation.mutate()}
      >
        {isWatching ? 'Не следить' : 'Следить'}
      </Button>

      {watchers.length > 0 ? (
        <div className={styles['watchers']} data-testid="watchers-list" aria-label="Наблюдатели">
          {watchers.map((watcher) => (
            <Tooltip key={watcher.user.id} content={`${watcher.user.displayName} — наблюдатель`}>
              <span className={styles['item']}>
                <Avatar user={watcher.user} size="xs" showTooltip={false} />
              </span>
            </Tooltip>
          ))}
        </div>
      ) : null}
    </div>
  );
}
